import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { NotificationService } from '../../shared/services/notification.service';
import { isEventNetworkingPublicUrl } from 'src/app/shared/utils/event-networking-url';
import { EMBEDGENERATOR, SMARTGOALSGENERATOR } from 'src/app/app-routing.module';

import { of } from 'rxjs';
import {
  ConversationLoad,
  ConversationLoadError,
  ConversationLoadSuccess,
  CONVERSATION_LOAD,
  CONVERSATION_LOAD_ERROR,
  CONVERSATION_LOAD_SUCCESS,
  ADD_COMMENT_TO_CONVERSATION,
  ADD_COMMENT_TO_CONVERSATION_ERROR,
  ADD_COMMENT_TO_CONVERSATION_SUCCESS,
  AddCommentToConversation,
  AddCommentToConversationSuccess,
  AddCommentToConversationError,
  UPDATE_CONVERSATION,
  UpdateConversation,
  UPDATE_CONVERSATION_SUCCESS,
  UPDATE_CONVERSATION_ERROR,
  UpdateConversationError,
  UpdateConversationSuccess,
} from '../actions/conversation.actions';
import { Conversation } from 'src/app/shared/types/conversation.types';
import { ConversationService } from 'src/app/shared/services/conversation.service';
import { CommentService } from 'src/app/shared/services/comment.service';

@Injectable()
export class ConversationEffects {
  private readonly publicNoAuthPaths = [`/${SMARTGOALSGENERATOR}`, `/${EMBEDGENERATOR}`];
  private readonly authUiRoots = ['/auth', '/users'];

  public conversationLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ConversationLoad>(CONVERSATION_LOAD),
      mergeMap(() => {
        const fromRouter = this.router.url.split('?')[0];
        const fromLocation = typeof window !== 'undefined' ? window.location.pathname : '';
        const isAuthUiRoute =
          this.authUiRoots.some(root => fromRouter === root || fromRouter.startsWith(root + '/')) ||
          this.authUiRoots.some(root => fromLocation === root || fromLocation.startsWith(root + '/'));
        const isPublicNoAuthRoute =
          this.publicNoAuthPaths.includes(fromRouter) ||
          this.publicNoAuthPaths.includes(fromLocation) ||
          isEventNetworkingPublicUrl(fromRouter) ||
          isEventNetworkingPublicUrl(fromLocation);

        // Never load conversations on login/signup/password-reset routes.
        // This prevents confusing auth errors before the user is actually logged in.
        if (isPublicNoAuthRoute || isAuthUiRoute) {
          return of(new ConversationLoadSuccess([]));
        }
        this.notificationService.loading = true;
        return this.conversationService.getConversations().pipe(
          map(data => {
            const conversations: Conversation[] = data;
            return new ConversationLoadSuccess(conversations);
          }),
          catchError(err => {
            const status = err?.status;
            const apiCode = err?.error?.code || err?.error?.detail?.code;
            const isAuthError = status === 401 || status === 403 || apiCode === 'token_not_valid';
            // Auth errors are handled globally (logout/redirect). Don't show "find failed" snackbars.
            if (isAuthError) {
              return of(new ConversationLoadSuccess([]));
            }
            return of(new ConversationLoadError());
          })
        );
      })
    )
  );
  public conversationLoadSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ConversationLoadSuccess>(CONVERSATION_LOAD_SUCCESS),
        tap(() => (this.notificationService.loading = false))
      ),
    { dispatch: false }
  );

  public conversationLoadError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ConversationLoadError>(CONVERSATION_LOAD_ERROR),
        tap(() => (this.notificationService.loading = false)),
        tap(() => this.notificationService.openSnackBar('error', 'COMMON.CONVERSATION_FIND_FAILED'))
      ),
    { dispatch: false }
  );

  // addComment to conversation
  public addCommentToConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddCommentToConversation>(ADD_COMMENT_TO_CONVERSATION),
      tap(() => (this.notificationService.loading = true)),
      mergeMap(payload =>
        this.commentService.addComment(payload.conversation, payload.comment).pipe(
          map(conversation => {
            return new AddCommentToConversationSuccess(conversation);
          }),
          catchError(() => of(new AddCommentToConversationError(payload.conversation.id)))
        )
      )
    )
  );

  public addCommentToConversationSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<AddCommentToConversationSuccess>(ADD_COMMENT_TO_CONVERSATION_SUCCESS),
        tap(() => (this.notificationService.loading = false))
      ),
    { dispatch: false }
  );

  public addCommentToConversationError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<AddCommentToConversationError>(ADD_COMMENT_TO_CONVERSATION_ERROR),
        tap(() => (this.notificationService.loading = false)),
        tap(() => this.notificationService.openSnackBar('error', 'COMMON.CONVERSATION_UPDATE_FAILED'))
      ),
    { dispatch: false }
  );

  // update conversation
  public updateConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateConversation>(UPDATE_CONVERSATION),
      tap(() => (this.notificationService.loading = true)),
      mergeMap(payload =>
        this.conversationService.updateConversation(payload.conversationId, payload.conversation).pipe(
          map(conversation => {
            return new UpdateConversationSuccess(conversation);
          }),
          catchError(() => of(new UpdateConversationError()))
        )
      )
    )
  );

  public updateConversationSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateConversationSuccess>(UPDATE_CONVERSATION_SUCCESS),
        tap(() => (this.notificationService.loading = false))
      ),
    { dispatch: false }
  );

  public updateConversationError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<UpdateConversationError>(UPDATE_CONVERSATION_ERROR),
        tap(() => (this.notificationService.loading = false)),
        tap(() => this.notificationService.openSnackBar('error', 'COMMON.CONVERSATION_UPDATING_FAILED'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private conversationService: ConversationService,
    private commentService: CommentService,
    private router: Router
  ) {}
}
